import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

/**
 * Phase 4.1 — CollabSuggestions
 * Shows 3-6 artists to collaborate with based on genre/engagement.
 * Used on Dashboard (no genre prop) and TrackPage (with genre prop).
 */
export const CollabSuggestions = ({ genre, limit = 6, compact = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    if (genre) params.set("genre", genre);
    api.get(`/collab-suggestions?${params.toString()}`)
      .then((r) => { if (!cancelled) setItems(r.data?.suggestions || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [genre, limit]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-purple-500/20 bg-black/30 p-5" data-testid="collab-suggestions-loading">
        <div className="h-4 w-40 bg-gray-800 rounded animate-pulse mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 bg-gray-800/60 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div
      className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-black/40 p-5"
      data-testid="collab-suggestions"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">🤝</span>
        <h3 className="text-white font-bold text-sm tracking-wide">
          {compact ? "Collab Picks" : "Suggested Collaborations"}
        </h3>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">AI</span>
      </div>
      <div className={`grid gap-3 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
        {items.map((s) => (
          <Link
            key={s.user_id}
            to={`/artist/${s.user_id}`}
            data-testid={`collab-suggestion-${s.user_id}`}
            className="flex items-center gap-3 p-3 rounded-lg bg-gray-900/40 hover:bg-gray-900/70 border border-gray-800 transition-all"
          >
            <img
              src={getProfilePhotoUrl(s)}
              alt={s.artist_name || "artist"}
              className="w-10 h-10 rounded-full object-cover"
              onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop"; }}
            />
            <div className="min-w-0 flex-1">
              <p className="text-white font-medium truncate text-sm">{s.artist_name}</p>
              <p className="text-gray-500 text-xs truncate">{s.reason}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default CollabSuggestions;
